import React, { useState, useEffect, useCallback } from "react";
import {
  FaPlus,
  FaSearch,
  FaEdit,
  FaTrash,
  //FaEye,
  FaDownload,
  FaFilter,
  FaChevronRight,
  FaChevronDown,
  //FaHome,
  FaUpload,
  FaBook,
  FaList,
  FaLayerGroup,
  FaFileAlt
} from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import accountingService from "../../services/accountingService";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import Modal from "../../components/common/Modal";
import ClasseForm from "../../components/accounting/ClasseForm";
import RubriqueForm from "../../components/accounting/RubriqueForm";
import CompteForm from "../../components/accounting/CompteForm";

const PlanComptableView = () => {
  const { hasPermission, currentUser } = useAuth();

  // États principaux
  const [activeView, setActiveView] = useState("plan-comptable"); // Vue par défaut
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedClasse, setSelectedClasse] = useState("");
  const [viewMode, setViewMode] = useState("hierarchique"); // 'hierarchique' ou 'tableau'

  // États pour l'importation
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importLoading, setImportLoading] = useState(false);

  // Données
  const [classes, setClasses] = useState([]);
  const [rubriques, setRubriques] = useState([]);
  const [comptes, setComptes] = useState([]);
  const [planComptable, setPlanComptable] = useState([]);
  const [searchResults, setSearchResults] = useState(null);

  // États des modals
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [editingItem, setEditingItem] = useState(null);

  // États d'affichage hiérarchique
  const [expandedItems, setExpandedItems] = useState({});

  // Messages d'état
  const [message, setMessage] = useState({ type: "", text: "" });

  // Configuration des vues avec sous-menus
  const subMenuItems = [
    {
      id: 'plan-comptable',
      name: 'Plan de comptes',
      icon: FaBook,
      description: 'Vue hiérarchique complète du plan comptable OHADA'
    },
    {
      id: 'classes',
      name: 'Classes',
      icon: FaLayerGroup,
      description: 'Gestion des classes comptables (niveau 1)'
    },
    {
      id: 'rubriques',
      name: 'Rubriques',
      icon: FaList,
      description: 'Gestion des rubriques comptables (niveau 2)'
    },
    {
      id: 'comptes',
      name: 'Comptes',
      icon: FaFileAlt,
      description: 'Gestion des comptes comptables (niveaux 3-5)'
    }
  ];

  // =============================================================================
  // FONCTIONS DE CHARGEMENT DES DONNÉES
  // =============================================================================

  const loadPlanComptable = useCallback(async () => {
    try {
      const response = await accountingService.getPlanComptable();
      let planData = response.data || [];

      if (selectedClasse) {
        planData = planData.filter(
          (item) => item.classe._id === selectedClasse
        );
      }

      setPlanComptable(planData);
    } catch (error) {
      console.error("Erreur plan comptable:", error);
      showMessage("error", "Erreur lors du chargement du plan comptable");
    }
  }, [selectedClasse]);

  const handleSearch = useCallback(async () => {
    if (searchTerm.length < 2) return;

    try {
      setLoading(true);
      const response = await accountingService.search(searchTerm);
      let searchData = response.data;

      if (selectedClasse) {
        searchData = {
          classes: searchData.classes.filter((c) => c._id === selectedClasse),
          rubriques: searchData.rubriques.filter(
            (r) => r.classe_id._id === selectedClasse
          ),
          comptes: searchData.comptes.filter(
            (c) => c.classe_id._id === selectedClasse
          ),
        };
      }

      setSearchResults(searchData);
    } catch (error) {
      console.error("Erreur de recherche:", error);
      showMessage("error", "Erreur lors de la recherche");
    } finally {
      setLoading(false);
    }
  }, [searchTerm, selectedClasse]);

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const [classesRes, rubriquesRes, comptesRes] = await Promise.all([
        accountingService.getClasses(),
        accountingService.getRubriques(),
        accountingService.getComptes(),
      ]);

      setClasses(classesRes.data || []);
      setRubriques(rubriquesRes.data || []);
      setComptes(comptesRes.data || []);

      await loadPlanComptable();
    } catch (error) {
      console.error("Erreur lors du chargement:", error);
      showMessage("error", "Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  }, [loadPlanComptable]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (searchTerm.length >= 2) {
      handleSearch();
    } else {
      setSearchResults(null);
      if (activeView === "plan-comptable") {
        loadPlanComptable();
      }
    }
  }, [searchTerm, selectedClasse, activeView, handleSearch, loadPlanComptable]);

  const getFilteredData = useCallback(() => {
    let data = [];

    switch (activeView) {
      case "classes":
        data = classes;
        break;
      case "rubriques":
        data = rubriques;
        if (selectedClasse) {
          data = data.filter((r) => r.classe_id._id === selectedClasse);
        }
        break;
      case "comptes":
        data = comptes;
        if (selectedClasse) {
          data = data.filter((c) => c.classe_id._id === selectedClasse);
        }
        break;
      default:
        data = [];
    }

    return data;
  }, [activeView, classes, rubriques, comptes, selectedClasse]);

  // =============================================================================
  // FONCTIONS DE GESTION DES MODALS
  // =============================================================================

  const openModal = (type, item = null) => {
    setModalType(type);
    setEditingItem(item);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setModalType("");
    setEditingItem(null);
  };

  // =============================================================================
  // FONCTIONS CRUD
  // =============================================================================

  const handleSaveClasse = async (classeData) => {
    try {
      setLoading(true);

      if (editingItem) {
        await accountingService.updateClasse(editingItem._id, classeData);
        showMessage("success", "Classe modifiée avec succès");
      } else {
        await accountingService.createClasse(classeData);
        showMessage("success", "Classe créée avec succès");
      }

      closeModal();
      await loadInitialData();
    } catch (error) {
      console.error("Erreur classe:", error);
      showMessage("error", error.message || "Erreur lors de la sauvegarde");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRubrique = async (rubriqueData) => {
    try {
      setLoading(true);

      if (editingItem) {
        await accountingService.updateRubrique(editingItem._id, rubriqueData);
        showMessage("success", "Rubrique modifiée avec succès");
      } else {
        await accountingService.createRubrique(rubriqueData);
        showMessage("success", "Rubrique créée avec succès");
      }

      closeModal();
      await loadInitialData();
    } catch (error) {
      console.error("Erreur rubrique:", error);
      showMessage("error", error.message || "Erreur lors de la sauvegarde");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCompte = async (compteData) => {
    try {
      setLoading(true);

      if (editingItem) {
        await accountingService.updateCompte(editingItem._id, compteData);
        showMessage("success", "Compte modifié avec succès");
      } else {
        await accountingService.createCompte(compteData);
        showMessage("success", "Compte créé avec succès");
      }

      closeModal();
      await loadInitialData();
    } catch (error) {
      console.error("Erreur compte:", error);
      showMessage("error", error.message || "Erreur lors de la sauvegarde");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (type, item) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer cette ${type} ?`)) {
      return;
    }

    try {
      setLoading(true);

      switch (type) {
        case "classe":
          await accountingService.deleteClasse(item._id);
          break;
        case "rubrique":
          await accountingService.deleteRubrique(item._id);
          break;
        case "compte":
          await accountingService.deleteCompte(item._id);
          break;
        default:
          throw new Error(`Type non supporté: ${type}`);
      }

      showMessage(
        "success",
        `${type.charAt(0).toUpperCase() + type.slice(1)} supprimée avec succès`
      );
      await loadInitialData();
    } catch (error) {
      console.error("Erreur suppression:", error);
      showMessage("error", error.message || "Erreur lors de la suppression");
    } finally {
      setLoading(false);
    }
  };

  // =============================================================================
  // FONCTIONS UTILITAIRES
  // =============================================================================

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 5000);
  };

  const toggleExpanded = (id) => {
    setExpandedItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const exportToExcel = async () => {
    try {
      const response = await accountingService.getPlanComptable("flat");
      console.log("Données prêtes pour export:", response.data);
      showMessage("info", "Fonctionnalité d'export en développement");
    } catch (error) {
      showMessage("error", "Erreur lors de l'export");
    }
  };

  // Fonction d'importation
  const handleImportExcel = async () => {
    if (!importFile) {
      showMessage('error', 'Veuillez sélectionner un fichier');
      return;
    }

    console.log('Fichier sélectionné:', {
      name: importFile.name,
      size: importFile.size,
      type: importFile.type,
      lastModified: importFile.lastModified
    });

    try {
      setImportLoading(true);
      const result = await accountingService.importFromExcel(importFile);
      
      showMessage('success', `Importation réussie: ${result.data.total} éléments traités`);
      setShowImportModal(false);
      setImportFile(null);
      
      await loadInitialData();
      
    } catch (error) {
      console.error('Erreur importation complète:', error);
      console.error('Détails de l\'erreur:', error.response?.data);
      showMessage('error', error.message || error.response?.data?.message || 'Erreur lors de l\'importation');
    } finally {
      setImportLoading(false);
    }
  };

  // =============================================================================
  // COMPOSANTS DE RENDU - EN-TÊTES ET NAVIGATION
  // =============================================================================

  // Nouveau composant pour l'en-tête avec sous-menus
  const renderSubMenuHeader = () => {
    const currentView = subMenuItems.find(item => item.id === activeView);
    
    return (
      <div className="bg-white rounded-lg shadow mb-6">
        {/* En-tête avec titre de la vue active */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
            <div>
              <h2 className="text-xl font-bold text-primary-600 flex items-center">
                {currentView && <currentView.icon className="mr-1 text-primary-600" />}
                {currentView?.name || 'Plan Comptable'}
              </h2>
              <p className="text-primary-600 mt-1">
                {currentView?.description || 'Gestion du plan comptable OHADA'}
              </p>
            </div>
          </div>
        </div>

        {/* Sous-menu de navigation */}
        <div className="px-6">
          <nav className="flex space-x-8 overflow-x-auto">
            {subMenuItems.map((item) => {
              const IconComponent = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => setActiveView(item.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                    activeView === item.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <IconComponent />
                  <span>{item.name}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>
    );
  };

  const renderSearchAndFilters = () => (
    <div className="bg-white p-4 rounded-lg shadow mb-6">
      <div className="flex flex-col md:flex-row gap-4">
        {/* Barre de recherche */}
        <div className="flex-1 relative">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher par code ou libellé..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        {/* Filtre par classe */}
        <div className="flex items-center space-x-2">
          <FaFilter className="text-gray-400" />
          <select
            value={selectedClasse}
            onChange={(e) => {
              console.log("Classe sélectionnée:", e.target.value);
              setSelectedClasse(e.target.value);
            }}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Toutes les classes</option>
            {classes.map((classe) => (
              <option key={classe._id} value={classe._id}>
                Classe {classe.code} - {classe.libelle}
              </option>
            ))}
          </select>
          {selectedClasse && (
            <button
              onClick={() => setSelectedClasse("")}
              className="px-2 py-1 bg-primary-100 text-primary-700 rounded text-sm hover:bg-primary-200"
              title="Supprimer le filtre"
            >
              Filtré ✕
            </button>
          )}
        </div>

        {/* Boutons d'action */}
        <div className="flex space-x-2">
          {/* Bouton d'importation */}
          {(hasPermission("canManageUsers") ||
            ["admin", "admin_assistant"].includes(currentUser?.role)) && (
            <button
              onClick={() => setShowImportModal(true)}
              className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              title="Importer depuis Excel"
            >
              <FaUpload />
              <span>Importer</span>
            </button>
          )}

          {/* Boutons spécifiques au plan comptable */}
          {activeView === "plan-comptable" && (
            <div className="flex space-x-2">
              <button
                onClick={() =>
                  setViewMode(
                    viewMode === "hierarchique" ? "tableau" : "hierarchique"
                  )
                }
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                title={`Basculer en vue ${
                  viewMode === "hierarchique" ? "tableau" : "hiérarchique"
                }`}
              >
                Vue {viewMode === "hierarchique" ? "Tableau" : "Hiérarchique"}
              </button>
              <button
                onClick={exportToExcel}
                className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                title="Exporter vers Excel"
              >
                <FaDownload />
                <span>Export</span>
              </button>
            </div>
          )}

          {/* Bouton Nouveau */}
          {(hasPermission("canManageUsers") ||
            ["admin", "admin_assistant"].includes(currentUser?.role)) && (
            <button
              onClick={() => {
                const type =
                  activeView === "classes"
                    ? "classe"
                    : activeView === "rubriques"
                    ? "rubrique"
                    : activeView === "comptes"
                    ? "compte"
                    : "compte";
                openModal(type);
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
              title={`Créer un nouvel élément`}
            >
              <FaPlus />
              <span>Nouveau</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
  // =============================================================================
  // COMPOSANTS DE RENDU - CONTENU PRINCIPAL
  // =============================================================================

  const renderSearchResults = () => {
    if (!searchResults) return null;

    const {
      classes: foundClasses,
      rubriques: foundRubriques,
      comptes: foundComptes,
    } = searchResults;
    const totalFound =
      foundClasses.length + foundRubriques.length + foundComptes.length;

    if (totalFound === 0) {
      return (
        <div className="bg-white p-8 rounded-lg shadow text-center">
          <p className="text-gray-500">
            Aucun résultat trouvé pour "{searchTerm}"
          </p>
          {selectedClasse && (
            <p className="text-sm text-gray-400 mt-1">
              (filtré par classe sélectionnée)
            </p>
          )}
        </div>
      );
    }

    return (
      <div className="space-y-6">
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-medium mb-2">
            Résultats de recherche ({totalFound} éléments trouvés)
          </h3>
          <p className="text-gray-600">
            Recherche pour: "{searchTerm}"
            {selectedClasse && (
              <span className="text-sm text-gray-500 ml-2">
                (filtré par classe)
              </span>
            )}
          </p>
        </div>

        {/* Classes trouvées */}
        {foundClasses.length > 0 && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h4 className="text-md font-medium text-blue-800">
                Classes ({foundClasses.length})
              </h4>
            </div>
            <div className="divide-y divide-gray-200">
              {foundClasses.map((classe) => (
                <div
                  key={classe._id}
                  className="px-6 py-4 flex items-center justify-between"
                >
                  <div>
                    <span className="font-medium">Classe {classe.code}</span>
                    <span className="ml-2 text-gray-600">{classe.libelle}</span>
                  </div>
                  {(hasPermission("canManageUsers") ||
                    currentUser?.role === "admin") && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openModal("classe", classe)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <FaEdit />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Rubriques trouvées */}
        {foundRubriques.length > 0 && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h4 className="text-md font-medium text-green-800">
                Rubriques ({foundRubriques.length})
              </h4>
            </div>
            <div className="divide-y divide-gray-200">
              {foundRubriques.map((rubrique) => (
                <div
                  key={rubrique._id}
                  className="px-6 py-4 flex items-center justify-between"
                >
                  <div>
                    <span className="font-medium">{rubrique.code}</span>
                    <span className="ml-2 text-gray-600">
                      {rubrique.libelle}
                    </span>
                    <span className="ml-4 text-sm text-gray-500">
                      (Classe {rubrique.classe_id?.code})
                    </span>
                  </div>
                  {(hasPermission("canManageUsers") ||
                    ["admin", "admin_assistant"].includes(
                      currentUser?.role
                    )) && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openModal("rubrique", rubrique)}
                        className="text-green-600 hover:text-green-800"
                      >
                        <FaEdit />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Comptes trouvés */}
        {foundComptes.length > 0 && (
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h4 className="text-md font-medium text-yellow-800">
                Comptes ({foundComptes.length})
              </h4>
            </div>
            <div className="divide-y divide-gray-200">
              {foundComptes.map((compte) => (
                <div
                  key={compte._id}
                  className="px-6 py-4 flex items-center justify-between"
                >
                  <div>
                    <span className="font-medium">{compte.code}</span>
                    <span className="ml-2 text-gray-600">{compte.libelle}</span>
                    <div className="text-sm text-gray-500 mt-1">
                      Niveau {compte.niveau} - Classe {compte.classe_id?.code}
                      {compte.rubrique_id &&
                        ` - Rubrique ${compte.rubrique_id.code}`}
                    </div>
                  </div>
                  {(hasPermission("canManageUsers") ||
                    ["admin", "admin_assistant"].includes(
                      currentUser?.role
                    )) && (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openModal("compte", compte)}
                        className="text-yellow-600 hover:text-yellow-800"
                      >
                        <FaEdit />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPlanComptableHierarchique = () => {
    if (!planComptable.length) {
      return (
        <div className="bg-white p-8 rounded-lg shadow text-center">
          <p className="text-gray-500">
            {selectedClasse
              ? "Aucune donnée pour cette classe"
              : "Aucune donnée disponible"}
          </p>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {planComptable.map(({ classe, rubriques, comptes }) => (
          <div
            key={classe._id}
            className="bg-white rounded-lg shadow overflow-hidden"
          >
            {/* En-tête de classe */}
            <div
              className="bg-blue-50 border-l-4 border-blue-500 p-4 cursor-pointer hover:bg-blue-100 transition-colors"
              onClick={() => toggleExpanded(`classe-${classe._id}`)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  {expandedItems[`classe-${classe._id}`] ? (
                    <FaChevronDown />
                  ) : (
                    <FaChevronRight />
                  )}
                  <span className="font-bold text-blue-800">
                    Classe {classe.code} - {classe.libelle}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-blue-600">
                    {rubriques.length} rubriques, {comptes.length} comptes
                  </span>
                  {(hasPermission("canManageUsers") ||
                    currentUser?.role === "admin") && (
                    <div className="flex space-x-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openModal("classe", classe);
                        }}
                        className="p-1 text-blue-600 hover:bg-blue-200 rounded"
                      >
                        <FaEdit size={14} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete("classe", classe);
                        }}
                        className="p-1 text-red-600 hover:bg-red-200 rounded"
                      >
                        <FaTrash size={14} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Contenu développable de la classe */}
            {expandedItems[`classe-${classe._id}`] && (
              <div className="p-4 space-y-4">
                {/* Rubriques */}
                {rubriques.map((rubrique) => (
                  <div key={rubrique._id} className="ml-4">
                    <div
                      className="bg-green-50 border-l-4 border-green-400 p-3 cursor-pointer hover:bg-green-100 transition-colors"
                      onClick={() => toggleExpanded(`rubrique-${rubrique._id}`)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          {expandedItems[`rubrique-${rubrique._id}`] ? (
                            <FaChevronDown />
                          ) : (
                            <FaChevronRight />
                          )}
                          <span className="font-medium text-green-800">
                            {rubrique.code} - {rubrique.libelle}
                          </span>
                        </div>
                        {(hasPermission("canManageUsers") ||
                          ["admin", "admin_assistant"].includes(
                            currentUser?.role
                          )) && (
                          <div className="flex space-x-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                openModal("rubrique", rubrique);
                              }}
                              className="p-1 text-green-600 hover:bg-green-200 rounded"
                            >
                              <FaEdit size={12} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete("rubrique", rubrique);
                              }}
                              className="p-1 text-red-600 hover:bg-red-200 rounded"
                            >
                              <FaTrash size={12} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Comptes de cette rubrique */}
                    {expandedItems[`rubrique-${rubrique._id}`] && (
                      <div className="ml-4 mt-2 space-y-1">
                        {comptes
                          .filter(
                            (compte) => compte.rubrique_id?._id === rubrique._id
                          )
                          .map((compte) => (
                            <div
                              key={compte._id}
                              className="p-2 rounded border-l-2 bg-yellow-50 border-l-yellow-400"
                            >
                              <div className="flex items-center justify-between">
                                <span className="text-sm">
                                  {"  ".repeat(compte.niveau - 2)}
                                  {compte.code} - {compte.libelle}
                                </span>
                                {(hasPermission("canManageUsers") ||
                                  ["admin", "admin_assistant"].includes(
                                    currentUser?.role
                                  )) && (
                                  <div className="flex space-x-1">
                                    <button
                                      onClick={() =>
                                        openModal("compte", compte)
                                      }
                                      className="p-1 text-yellow-600 hover:bg-yellow-200 rounded"
                                    >
                                      <FaEdit size={10} />
                                    </button>
                                    <button
                                      onClick={() =>
                                        handleDelete("compte", compte)
                                      }
                                      className="p-1 text-red-600 hover:bg-red-200 rounded"
                                    >
                                      <FaTrash size={10} />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                ))}

                {/* Comptes sans rubrique */}
                {comptes
                  .filter(
                    (compte) =>
                      !compte.rubrique_id && compte.classe_id._id === classe._id
                  )
                  .map((compte) => (
                    <div
                      key={compte._id}
                      className="ml-4 p-2 rounded border-l-2 bg-gray-50 border-l-gray-400"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm">
                          {"  ".repeat(compte.niveau - 1)}
                          {compte.code} - {compte.libelle}
                        </span>
                        {(hasPermission("canManageUsers") ||
                          ["admin", "admin_assistant"].includes(
                            currentUser?.role
                          )) && (
                          <div className="flex space-x-1">
                            <button
                              onClick={() => openModal("compte", compte)}
                              className="p-1 text-gray-600 hover:bg-gray-200 rounded"
                            >
                              <FaEdit size={10} />
                            </button>
                            <button
                              onClick={() => handleDelete("compte", compte)}
                              className="p-1 text-red-600 hover:bg-red-200 rounded"
                            >
                              <FaTrash size={10} />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderTableView = () => {
    const allItems = [];

    planComptable.forEach(({ classe, rubriques, comptes }) => {
      allItems.push({ ...classe, type: "classe", niveau: 1 });
      rubriques.forEach((rubrique) => {
        allItems.push({ ...rubrique, type: "rubrique", niveau: 2 });
      });
      comptes.forEach((compte) => {
        allItems.push({ ...compte, type: "compte" });
      });
    });

    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Niveau
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Libellé
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Classe
                </th>
                {(hasPermission("canManageUsers") ||
                  ["admin", "admin_assistant"].includes(currentUser?.role)) && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {allItems.map((item, index) => (
                <tr
                  key={`${item.type}-${item._id}`}
                  className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {"  ".repeat(item.niveau - 1)}
                    {item.code}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        item.niveau === 1
                          ? "bg-blue-100 text-blue-800"
                          : item.niveau === 2
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {item.niveau}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {"  ".repeat(item.niveau - 1)}
                    {item.libelle}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        item.type === "classe"
                          ? "bg-blue-100 text-blue-800"
                          : item.type === "rubrique"
                          ? "bg-green-100 text-green-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {item.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.type === "classe"
                      ? `${item.code} - ${item.libelle}`
                      : item.classe_id
                      ? `${item.classe_id.code} - ${item.classe_id.libelle}`
                      : "-"}
                  </td>
                  {(hasPermission("canManageUsers") ||
                    ["admin", "admin_assistant"].includes(
                      currentUser?.role
                    )) && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => openModal(item.type, item)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDelete(item.type, item)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderIndividualView = () => {
    const data = getFilteredData();

    if (!data.length) {
      return (
        <div className="bg-white p-8 rounded-lg shadow text-center">
          <p className="text-gray-500">
            {selectedClasse
              ? "Aucun élément trouvé pour cette classe"
              : "Aucun élément trouvé"}
          </p>
          {(hasPermission("canManageUsers") ||
            ["admin", "admin_assistant"].includes(currentUser?.role)) && (
            <button
              onClick={() =>
                openModal(
                  activeView.slice(0, -1) === "classe"
                    ? "classe"
                    : activeView.slice(0, -1)
                )
              }
              className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              Créer le premier élément
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Libellé
                </th>
                {activeView !== "classes" && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Classe
                  </th>
                )}
                {activeView === "comptes" && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Niveau
                  </th>
                )}
                {(hasPermission("canManageUsers") ||
                  ["admin", "admin_assistant"].includes(currentUser?.role)) && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.map((item, index) => (
                <tr
                  key={item._id}
                  className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {item.code}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {item.libelle}
                  </td>
                  {activeView !== "classes" && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {item.classe_id
                        ? `${item.classe_id.code} - ${item.classe_id.libelle}`
                        : "-"}
                    </td>
                  )}
                  {activeView === "comptes" && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          item.niveau === 1
                            ? "bg-blue-100 text-blue-800"
                            : item.niveau === 2
                            ? "bg-green-100 text-green-800"
                            : item.niveau === 3
                            ? "bg-yellow-100 text-yellow-800"
                            : item.niveau === 4
                            ? "bg-purple-100 text-purple-800"
                            : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {item.niveau}
                      </span>
                    </td>
                  )}
                  {(hasPermission("canManageUsers") ||
                    ["admin", "admin_assistant"].includes(
                      currentUser?.role
                    )) && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() =>
                            openModal(
                              activeView.slice(0, -1) === "classe"
                                ? "classe"
                                : activeView.slice(0, -1),
                              item
                            )
                          }
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() =>
                            handleDelete(
                              activeView.slice(0, -1) === "classe"
                                ? "classe"
                                : activeView.slice(0, -1),
                              item
                            )
                          }
                          className="text-red-600 hover:text-red-900"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) return <LoadingSpinner />;

    // Utiliser searchResults si une recherche est active
    if (searchResults) return renderSearchResults();

    if (activeView === "plan-comptable") {
      return viewMode === "hierarchique"
        ? renderPlanComptableHierarchique()
        : renderTableView();
    }

    // Utiliser renderIndividualView pour les vues individuelles
    return renderIndividualView();
  };

  // =============================================================================
  // RENDU PRINCIPAL
  // =============================================================================

  return (
    <div className="space-y-6">
      {/* Message de feedback */}
      {message.text && (
        <div
          className={`p-4 rounded-md ${
            message.type === "success"
              ? "bg-green-50 text-green-800"
              : message.type === "error"
              ? "bg-red-50 text-red-800"
              : "bg-blue-50 text-blue-800"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* En-tête avec sous-menus */}
      {renderSubMenuHeader()}

      {/* Barre de recherche et filtres */}
      {renderSearchAndFilters()}

      {/* Contenu principal */}
      {renderContent()}

      {/* Modal pour les formulaires */}
      {showModal && (
        <Modal
          isOpen={showModal}
          onClose={closeModal}
          title={`${editingItem ? "Modifier" : "Créer"} ${
            modalType === "classe"
              ? "une classe"
              : modalType === "rubrique"
              ? "une rubrique"
              : "un compte"
          }`}
        >
          {modalType === "classe" && (
            <ClasseForm
              classe={editingItem}
              onSave={handleSaveClasse}
              onCancel={closeModal}
              loading={loading}
            />
          )}
          {modalType === "rubrique" && (
            <RubriqueForm
              rubrique={editingItem}
              classes={classes}
              onSave={handleSaveRubrique}
              onCancel={closeModal}
              loading={loading}
            />
          )}
          {modalType === "compte" && (
            <CompteForm
              compte={editingItem}
              classes={classes}
              rubriques={rubriques}
              comptes={comptes}
              onSave={handleSaveCompte}
              onCancel={closeModal}
              loading={loading}
            />
          )}
        </Modal>
      )}

      {/* Modal d'importation */}
      {showImportModal && (
        <Modal
          isOpen={showImportModal}
          onClose={() => {
            setShowImportModal(false);
            setImportFile(null);
          }}
          title="Importer le plan comptable OHADA"
          size="large"
        >
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-md">
              <h4 className="font-medium text-blue-800 mb-2">
                Format de votre fichier :
              </h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>
                  • <strong>Code</strong> : Code de l'élément (1, 10, 101, 1011...)
                </li>
                <li>
                  • <strong>Niveau</strong> : 1=Classe, 2=Rubrique, 3-5=Comptes
                </li>
                <li>
                  • <strong>Libelle</strong> : Description de l'élément
                </li>
                <li>
                  • <strong>Code_Classe</strong> : Code de la classe parent
                </li>
                <li>
                  • <strong>Code_Rubrique</strong> : Code rubrique parent
                </li>
                <li>
                  • <strong>Code_Parent</strong> : Compte parent (hiérarchie)
                </li>
              </ul>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fichier Excel (.xlsx ou .xls)
              </label>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => setImportFile(e.target.files[0])}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => {
                  setShowImportModal(false);
                  setImportFile(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={importLoading}
              >
                Annuler
              </button>
              <button
                onClick={handleImportExcel}
                disabled={importLoading || !importFile}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {importLoading ? "Importation..." : "Importer"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PlanComptableView;